import { useEffect, useMemo, useRef, useState } from 'react'

import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'
import Form from 'react-bootstrap/Form'

import { Spinner } from '../../LoadingContainer/Spinner'
import { LoadingContainer } from '../../LoadingContainer'
import { Confirm } from '..'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import {
  getPackageMap,
  relocateSeasonPackageOrderSeats,
} from '../../../utilities/api'

import RelocatePackageSeatsMap from './RelocatePackageSeatsMap'
import {
  countSelectedSeats,
  trimSelectedSeatsToCount,
} from './relocatePackageSeatsMapHelpers'

const TUTORIAL_VIDEO_SOURCE =
  'https://blocktickets.nyc3.cdn.digitaloceanspaces.com/tutorials/Relocate%20Seats_1080p.mp4'

const sortTickets = (tickets = []) => {
  const safe = (v) => String(v ?? '')
  return [...tickets].sort((a, b) => {
    const aKey = `${safe(a.sectionNumber)}-${safe(a.rowNumber)}-${safe(a.seatNumber)}`
    const bKey = `${safe(b.sectionNumber)}-${safe(b.rowNumber)}-${safe(b.seatNumber)}`
    return aKey.localeCompare(bKey, undefined, { numeric: true })
  })
}

const formatPackageTicketSeat = (ticket) => {
  if (ticket?.GA) return `Sec ${ticket.sectionNumber} (GA)`
  return `Sec ${ticket.sectionNumber}, Row ${ticket.rowNumber}, Seat ${ticket.seatNumber}`
}

const formatTargetSeatLabel = (seatId, mapData) => {
  const seat = mapData?.seats?.[seatId]
  if (!seat) return `Seat ${seatId}`
  if (seat?.generalAdmission) return `Sec ${seat.sectionNumber} (GA)`
  return `Sec ${seat.sectionNumber}, Row ${seat.rowNumber}, Seat ${seat.seatNumber}`
}

const normalizeSeatId = (seatId) => String(seatId ?? '').trim()

export default function RelocateSeasonSeats({ order, onSuccess }) {
  const { closeModal, openModal } = useModal()

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [alert, setAlert] = useState({ show: false, message: '' })

  const [eventPackage, setEventPackage] = useState(null)
  const [mapData, setMapData] = useState(null)
  const [background, setBackground] = useState(null)
  const [soldMap, setSoldMap] = useState(new Map())
  const [reservedMap, setReservedMap] = useState(new Map())

  const [selectedFromIds, setSelectedFromIds] = useState(new Set())
  const [selectedToSeatIds, setSelectedToSeatIds] = useState({})
  const [showTutorial, setShowTutorial] = useState(false)
  const tutorialVideoRef = useRef(null)

  const handleCloseTutorial = () => {
    if (tutorialVideoRef.current) {
      tutorialVideoRef.current.pause()
      tutorialVideoRef.current.currentTime = 0
    }
    setShowTutorial(false)
  }

  const packageTickets = useMemo(
    () => sortTickets(order?.package_tickets || []),
    [order?.package_tickets]
  )

  const selectedFromTickets = useMemo(
    () => packageTickets.filter((ticket) => selectedFromIds.has(ticket.id)),
    [packageTickets, selectedFromIds]
  )

  const selectedFromCount = selectedFromTickets.length
  const selectedToCount = countSelectedSeats(selectedToSeatIds)
  const canRelocate =
    selectedFromCount > 0 &&
    selectedToCount > 0 &&
    selectedFromCount === selectedToCount

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true)
        const res = await getPackageMap(order?.package?.uuid, {
          includeSeatIdentity: false,
        })
        const {
          eventPackage: loadedPackage,
          soldSeatIds,
          reservedSeatIds,
          seatIdentityById,
        } = res?.data || {}

        setEventPackage(loadedPackage)
        setMapData(loadedPackage?.seatmap?.mapping || null)
        setBackground(loadedPackage?.seatmap?.background || null)

        const soldEntries = Array.from(soldSeatIds || [])
          .map((seatId) => normalizeSeatId(seatId))
          .filter(Boolean)
          .map((seatId) => [seatId, seatIdentityById?.[seatId] || true])
        const reservedEntries = Array.from(reservedSeatIds || [])
          .map((seatId) => normalizeSeatId(seatId))
          .filter(Boolean)
          .map((seatId) => [seatId, seatIdentityById?.[seatId] || true])
        setSoldMap(new Map(soldEntries))
        setReservedMap(new Map(reservedEntries))

        setSelectedFromIds(new Set())
        setSelectedToSeatIds({})
      } catch (err) {
        setAlert({
          show: true,
          message:
            err?.response?.data?.error?.message ||
            'Could not load package seat map. Please try again.',
        })
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [order?.id, order?.package?.uuid, order?.package_tickets])

  const toggleFromTicket = (ticketId) => {
    const nextSelectedFromIds = new Set(selectedFromIds)
    if (nextSelectedFromIds.has(ticketId)) nextSelectedFromIds.delete(ticketId)
    else nextSelectedFromIds.add(ticketId)

    setSelectedFromIds(nextSelectedFromIds)

    setSelectedToSeatIds((prev) =>
      trimSelectedSeatsToCount(prev, nextSelectedFromIds.size)
    )
  }

  const getTargetSeatLabels = () => {
    const seatIds = [
      ...Object.keys(selectedToSeatIds?.seats || {}),
      ...Object.values(selectedToSeatIds?.gaSeats || {}).flat(),
    ]
    return seatIds.map((seatId) => formatTargetSeatLabel(seatId, mapData))
  }

  const openConfirm = () => {
    if (!canRelocate) return

    const fromSeatLabels = selectedFromTickets
      .map((ticket) => formatPackageTicketSeat(ticket))
      .join('; ')
    const toSeatLabels = getTargetSeatLabels().join('; ')

    openModal(
      Confirm,
      {
        title: 'Relocate selected seats?',
        description: `From: ${fromSeatLabels}. To: ${toSeatLabels}. This replaces only the checked current seats on this order.`,
        onConfirm: () => {
          closeModal()
          handleRelocate()
        },
        onClose: closeModal,
      },
      { className: 'modal-md', contentClassName: '' },
      { replace: false }
    )
  }

  const handleRelocate = async () => {
    try {
      setIsSaving(true)
      setAlert({ show: false, message: '' })

      await relocateSeasonPackageOrderSeats({
        orderId: order?.id,
        fromPackageTicketIds: Array.from(selectedFromIds),
        selectedSeats: selectedToSeatIds,
      })

      if (typeof onSuccess === 'function') onSuccess()
      closeModal()
    } catch (err) {
      setAlert({
        show: true,
        message:
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Unable to relocate seats. Please try again.',
      })
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Modal.Body>
        <LoadingContainer />
      </Modal.Body>
    )
  }

  return (
    <>
      <Modal.Body className="d-flex-column">
        <div className="modal-body-heading">
          <div className="section-heading section-heading--secondary">
            <div className="d-flex align-items-center justify-content-between gap-2">
              <h1 className="mt-2 mb-0">Relocate Seats</h1>
            </div>
            <p className="section-header-desc mt-1 mb-0">
              Select the current seats to relocate, then pick the same number of
              new seats on the map.
            </p>
            <p className="section-header-desc mt-2">
              <strong>Note: </strong>
              <Button
                variant="link"
                className="p-0 align-baseline"
                onClick={() => setShowTutorial(true)}
              >
                View Tutorial
              </Button>
            </p>
          </div>
        </div>

        {alert.show && (
          <Alert variant="danger" className="mb-3">
            <p className="mb-0">{alert.message}</p>
          </Alert>
        )}

        <Stack gap={3}>
          <Stack gap={2}>
            <h3 className="small-bold mb-0">Current Seats</h3>
            <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
              {packageTickets.map((ticket) => (
                <Form.Check
                  key={ticket.id}
                  id={`relocate-seat-${ticket.id}`}
                  checked={selectedFromIds.has(ticket.id)}
                  onChange={() => toggleFromTicket(ticket.id)}
                  className="mb-2"
                  label={formatPackageTicketSeat(ticket)}
                />
              ))}
            </div>
          </Stack>

          <div className="small">
            Selected to relocate: <strong>{selectedFromCount}</strong> seat
            {selectedFromCount === 1 ? '' : 's'}
            {' | '}
            New seats selected: <strong>{selectedToCount}</strong>
          </div>

          {selectedFromCount > 0 && mapData && background ? (
            <RelocatePackageSeatsMap
              data={mapData}
              background={background}
              eventPackage={eventPackage}
              selectedSeatIds={selectedToSeatIds}
              setSelectedSeatIds={setSelectedToSeatIds}
              soldMap={soldMap}
              reservedMap={reservedMap}
              maxSelectable={selectedFromCount}
            />
          ) : (
            <Alert variant="light" className="mb-0">
              <p className="mb-0">
                Select at least one current seat to enable the map.
              </p>
            </Alert>
          )}

          {selectedFromCount > 0 && selectedToCount !== selectedFromCount && (
            <Alert variant="warning" className="mb-0">
              <p className="mb-0">
                New seat selection must match the number of seats being
                relocated.
              </p>
            </Alert>
          )}
        </Stack>

        <Stack
          direction="horizontal"
          className="btn-group-flex justify-content-end mt-4"
        >
          <Button variant="default" size="lg" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            size="lg"
            disabled={!canRelocate || isSaving}
            onClick={openConfirm}
          >
            {isSaving ? <Spinner /> : 'Relocate Seats'}
          </Button>
        </Stack>
      </Modal.Body>

      <Modal
        show={showTutorial}
        onHide={handleCloseTutorial}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Relocate Seats Tutorial</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <video
            ref={tutorialVideoRef}
            controls
            autoPlay
            style={{ width: '100%' }}
          >
            <source src={TUTORIAL_VIDEO_SOURCE} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Modal.Body>
      </Modal>
    </>
  )
}
